/**
 * React Query Hook for fetching cases list
 */

import { useQuery } from '@tanstack/react-query';
import { getCases, getParentCases } from '@/api/services/casesApi';
import type { GetCasesParams, GetParentCasesParams } from '@/types/api';

/**
 * Query hook for fetching cases with filters and pagination
 * Uses TanStack Query with caching strategy
 * Cache key: ['cases', params]
 */
export const useCases = (params?: GetCasesParams) => {
  return useQuery({
    queryKey: ['cases', params],
    queryFn: async () => {
      return getCases(params);
    },
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });
};

/**
 * Query hook for fetching the authenticated parent's own cases
 * Server handles parent scoping and tab-based status filtering
 * Cache key: ['parentCases', params]
 */
export const useParentCases = (params?: GetParentCasesParams, enabled = true) => {
  return useQuery({
    queryKey: ['parentCases', params],
    queryFn: () => getParentCases(params),
    enabled,
    retry: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
};
